import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "@tanstack/react-router";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import React, { useState } from "react";
import { INVENTORY_DETAILS, INVENTORY_ORDER } from "../-data/inventory-data";

type InventoryDetailViewProps = {
    itemId: string;
};

const InventoryDetailView: React.FC<InventoryDetailViewProps> = ({ itemId }) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<"activity" | "metrics" | "stocks" | "ufp">("activity");
    const [rightPanelTab, setRightPanelTab] = useState<"overview" | "identifiers">("overview");

    const item = INVENTORY_DETAILS[itemId];
    const currentIndex = INVENTORY_ORDER.indexOf(itemId);
    const isFirst = currentIndex === 0;
    const isLast = currentIndex === INVENTORY_ORDER.length - 1;

    const handleNavigateUp = () => {
        if (!isFirst) {
            const prevItemId = INVENTORY_ORDER[currentIndex - 1];
            navigate({ to: `/manager/inventory/${prevItemId}` });
        }
    };

    const handleNavigateDown = () => {
        if (!isLast) {
            const nextItemId = INVENTORY_ORDER[currentIndex + 1];
            navigate({ to: `/manager/inventory/${nextItemId}` });
        }
    };

    const handleClose = () => {
        navigate({ to: '/manager/inventory' });
    };

    if (!item) {
        return <div className="p-6">Item not found</div>;
    }

    const getAvailabilityBadge = (availability: string) => {
        switch (availability) {
            case "In Stock":
                return <Badge className="bg-green-600 hover:bg-green-700 text-white">{availability}</Badge>;
            case "Low Stock":
                return <Badge className="bg-orange-500 hover:bg-orange-600 text-white">{availability}</Badge>;
            case "Critical":
                return <Badge className="bg-red-600 hover:bg-red-700 text-white">{availability}</Badge>;
            case "Out of Stock":
                return <Badge variant="secondary">{availability}</Badge>;
            default:
                return <Badge variant="outline">{availability}</Badge>;
        }
    };

    return (
        <div className="w-full h-full flex flex-col bg-white">
            {/* Header with Navigation */}
            <div className="border-b px-6 py-4">
                <div className="flex flex-row items-center justify-between">
                    <div className="flex flex-row items-center gap-x-4">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleClose}
                            className="h-8 w-8"
                        >
                            <X className="h-5 w-5" />
                        </Button>
                        <div className="flex flex-row items-center gap-x-2">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={handleNavigateUp}
                                disabled={isFirst}
                                className="h-8 w-8"
                            >
                                <ChevronUp className="h-5 w-5" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={handleNavigateDown}
                                disabled={isLast}
                                className="h-8 w-8"
                            >
                                <ChevronDown className="h-5 w-5" />
                            </Button>
                            <h2 className="text-xl font-semibold">{item.itemName}</h2>
                        </div>
                    </div>
                </div>
            </div>

            {/* Item Info Row */}
            <div className="border-b px-6 py-4">
                <div className="flex flex-row items-center justify-between">
                    <div className="flex flex-row items-center gap-x-6">
                        <span className="text-sm text-gray-600">{item.sku}</span>
                        <span className="text-sm">|</span>
                        <span className="text-sm text-gray-600">{item.shelfRack}</span>
                    </div>
                    <div className="flex flex-row items-center gap-x-4">
                        <Button variant="outline" size="sm">
                            Create Requisition
                        </Button>
                        <Button variant="outline" size="sm">
                            Request Stock
                        </Button>
                        <Button variant="outline" size="sm">
                            Update Status
                        </Button>
                    </div>
                </div>
            </div>

            {/* Status Badge */}
            <div className="px-6 py-3 border-b">
                {getAvailabilityBadge(item.availability)}
            </div>

            {/* Tabs */}
            <div className="flex flex-row items-center justify-between w-full">
                <div className="flex flex-row gap-x-2 px-6 py-3 border-b border-r w-[60%]">
                    <Button
                        variant={activeTab === "activity" ? "outline" : "ghost"}
                        onClick={() => setActiveTab("activity")}
                        className="gap-x-2 font-normal"
                    >
                        Activity
                    </Button>
                    <Button
                        variant={activeTab === "metrics" ? "outline" : "ghost"}
                        onClick={() => setActiveTab("metrics")}
                        className="gap-x-2 font-normal"
                    >
                        Metrics
                    </Button>
                    <Button
                        variant={activeTab === "stocks" ? "outline" : "ghost"}
                        onClick={() => setActiveTab("stocks")}
                        className="gap-x-2 font-normal"
                    >
                        Stocks
                    </Button>
                    <Button
                        variant={activeTab === "ufp" ? "outline" : "ghost"}
                        onClick={() => setActiveTab("ufp")}
                        className="gap-x-2 font-normal"
                    >
                        UPP
                    </Button>
                </div>
                <div className="flex flex-row gap-x-2 px-6 py-3 border-b border-r w-[40%]">
                    <Button
                        variant={rightPanelTab === "overview" ? "outline" : "ghost"}
                        onClick={() => setRightPanelTab("overview")}
                        className="gap-x-2 font-normal"
                    >
                        Overview
                    </Button>
                    <Button
                        variant={rightPanelTab === "identifiers" ? "outline" : "ghost"}
                        onClick={() => setRightPanelTab("identifiers")}
                        className="gap-x-2 font-normal"
                    >
                        Product Identifiers (GTINs)
                    </Button>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 flex flex-row overflow-auto">
                {/* Left Column - Content based on active tab */}
                <div className="w-[60%] space-y-6 p-6 border-r">
                    {activeTab === "activity" && (
                        <>
                            <h3 className="text-lg font-semibold">Recent Activity</h3>
                            
                            {/* Year Header */}
                            <div className="text-gray-400 text-sm font-medium">2025</div>
                            
                            {/* Activity Timeline */}
                            <div className="space-y-6">
                                {/* This week section */}
                                <div>
                                    <div className="text-blue-600 text-sm font-medium mb-4">This week</div>
                                    
                                    {/* Stock Received */}
                                    <div className="flex flex-row items-start gap-x-3 mb-6">
                                        <div className="flex flex-col items-center">
                                            <div className="w-3 h-3 rounded-full bg-blue-500 mt-1"></div>
                                            <div className="w-0.5 flex-1 bg-gray-200 min-h-[120px]"></div>
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex flex-row items-center gap-x-2 mb-2">
                                                <span className="font-medium text-base">Stock Received</span>
                                                <span className="text-green-600 font-medium">+500 units</span>
                                            </div>
                                            <div className="flex flex-row items-center gap-x-3 mb-3">
                                                <Badge variant="secondary" className="text-xs">Stock Requisitioned</Badge>
                                                <span className="text-gray-500 text-sm">1 day ago</span>
                                            </div>
                                            
                                            {/* Sub-activities */}
                                            <div className="space-y-3 mt-4">
                                                <div className="border rounded-lg p-4 bg-white">
                                                    <div className="font-medium mb-1">Transfer Completed</div>
                                                    <div className="text-red-600 text-sm">-100 units</div>
                                                </div>
                                                <div className="border rounded-lg p-4 bg-white">
                                                    <div className="font-medium mb-1">Stock Return</div>
                                                    <div className="text-green-600 text-sm">+25 units</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* April section */}
                                <div>
                                    <div className="text-gray-400 text-sm font-medium mb-4">April</div>
                                    
                                    {/* Created Order */}
                                    <div className="flex flex-row items-start gap-x-3 mb-6">
                                        <div className="flex flex-col items-center">
                                            <div className="w-3 h-3 rounded-full bg-blue-500 mt-1"></div>
                                            <div className="w-0.5 flex-1 bg-gray-200 min-h-[100px]"></div>
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex flex-row items-center gap-x-2 mb-2">
                                                <span className="font-medium text-base">Created Order</span>
                                            </div>
                                            <div className="flex flex-row items-center gap-x-3 mb-3">
                                                <Badge variant="secondary" className="text-xs">Inventory Update</Badge>
                                            </div>
                                            
                                            {/* Sub-activities */}
                                            <div className="space-y-3 mt-4">
                                                <div className="border rounded-lg p-4 bg-white">
                                                    <div className="font-medium mb-1">Stock Received</div>
                                                    <div className="text-green-600 text-sm">+500 units</div>
                                                </div>
                                                <div className="border rounded-lg p-4 bg-white">
                                                    <div className="font-medium mb-1">Stock Requisitioned</div>
                                                    <div className="text-red-600 text-sm">-100 units</div>
                                                </div>
                                                <div className="border rounded-lg p-4 bg-white">
                                                    <div className="font-medium mb-1">Transfer Completed</div>
                                                    <div className="text-red-600 text-sm">-50 units</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {activeTab === "metrics" && (
                        <div className="text-center text-gray-500 py-12">
                            Metrics view - Coming soon
                        </div>
                    )}

                    {activeTab === "stocks" && (
                        <div className="text-center text-gray-500 py-12">
                            Stocks view - Coming soon
                        </div>
                    )}

                    {activeTab === "ufp" && (
                        <>
                            <h3 className="text-lg font-semibold">UPP Info</h3>
                            
                            <div className="space-y-4">
                                <div className="flex flex-row items-center gap-x-3">
                                    <div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center">
                                        <span className="text-xs">📅</span>
                                    </div>
                                    <div className="flex-1 flex flex-row items-center justify-between">
                                        <span className="text-sm text-gray-600">Last Change Date</span>
                                        <span className="text-sm font-medium">30/09/2024</span>
                                    </div>
                                </div>

                                <div className="flex flex-row items-center gap-x-3">
                                    <div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center">
                                        <span className="text-xs">📅</span>
                                    </div>
                                    <div className="flex-1 flex flex-row items-center justify-between">
                                        <span className="text-sm text-gray-600">UPP Effective Date</span>
                                        <span className="text-sm font-medium">1/04/2025</span>
                                    </div>
                                </div>

                                <div className="flex flex-row items-center gap-x-3">
                                    <div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center">
                                        <span className="text-xs">📅</span>
                                    </div>
                                    <div className="flex-1 flex flex-row items-center justify-between">
                                        <span className="text-sm text-gray-600">UPP Updated Date</span>
                                        <span className="text-sm font-medium">4/02/2025</span>
                                    </div>
                                </div>

                                <div className="flex flex-row items-center gap-x-3">
                                    <div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center">
                                        <span className="text-xs">👤</span>
                                    </div>
                                    <div className="flex-1 flex flex-row items-center justify-between">
                                        <span className="text-sm text-gray-600">Agent Name</span>
                                        <span className="text-sm font-medium">GULF DRUG L.L.C.</span>
                                    </div>
                                </div>

                                <div className="flex flex-row items-center gap-x-3">
                                    <div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center">
                                        <span className="text-xs">📋</span>
                                    </div>
                                    <div className="flex-1 flex flex-row items-center justify-between">
                                        <span className="text-sm text-gray-600">UPP Scope</span>
                                        <span className="text-sm font-medium">Yes</span>
                                    </div>
                                </div>

                                <div className="flex flex-row items-center gap-x-3">
                                    <div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center">
                                        <span className="text-xs">📋</span>
                                    </div>
                                    <div className="flex-1 flex flex-row items-center justify-between">
                                        <span className="text-sm text-gray-600">Included in Basic Drug Formulary</span>
                                        <span className="text-sm font-medium">No</span>
                                    </div>
                                </div>

                                <div className="flex flex-row items-center gap-x-3">
                                    <div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center">
                                        <span className="text-xs">💊</span>
                                    </div>
                                    <div className="flex-1 flex flex-row items-center justify-between">
                                        <span className="text-sm text-gray-600">Dispense Mode</span>
                                        <span className="text-sm font-medium">Prescription Only</span>
                                    </div>
                                </div>

                                <div className="flex flex-row items-center gap-x-3">
                                    <div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center">
                                        <span className="text-xs">📦</span>
                                    </div>
                                    <div className="flex-1 flex flex-row items-center justify-between">
                                        <span className="text-sm text-gray-600">Package Price to Public</span>
                                        <span className="text-sm font-medium">100</span>
                                    </div>
                                </div>

                                <div className="flex flex-row items-center gap-x-3">
                                    <div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center">
                                        <span className="text-xs">📦</span>
                                    </div>
                                    <div className="flex-1 flex flex-row items-center justify-between">
                                        <span className="text-sm text-gray-600">Package Price to Pharmacy</span>
                                        <span className="text-sm font-medium">75</span>
                                    </div>
                                </div>

                                <div className="flex flex-row items-center gap-x-3">
                                    <div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center">
                                        <span className="text-xs">💰</span>
                                    </div>
                                    <div className="flex-1 flex flex-row items-center justify-between">
                                        <span className="text-sm text-gray-600">Unit Price to Public</span>
                                        <span className="text-sm font-medium">30</span>
                                    </div>
                                </div>

                                <div className="flex flex-row items-center gap-x-3">
                                    <div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center">
                                        <span className="text-xs">💰</span>
                                    </div>
                                    <div className="flex-1 flex flex-row items-center justify-between">
                                        <span className="text-sm text-gray-600">Unit Price to Pharmacy</span>
                                        <span className="text-sm font-medium">15</span>
                                    </div>
                                </div>

                                <div className="flex flex-row items-center gap-x-3">
                                    <div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center">
                                        <span className="text-xs">📋</span>
                                    </div>
                                    <div className="flex-1 flex flex-row items-center justify-between">
                                        <span className="text-sm text-gray-600">Included in Thiqa/ABM Drug Formulary</span>
                                        <span className="text-sm font-medium">Yes</span>
                                    </div>
                                </div>

                                <div className="flex flex-row items-center gap-x-3">
                                    <div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center">
                                        <span className="text-xs">📋</span>
                                    </div>
                                    <div className="flex-1 flex flex-row items-center justify-between">
                                        <span className="text-sm text-gray-600">Included in ABM 1 Drug Formulary</span>
                                        <span className="text-sm font-medium">No</span>
                                    </div>
                                </div>

                                <div className="flex flex-row items-center gap-x-3">
                                    <div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center">
                                        <span className="text-xs">📋</span>
                                    </div>
                                    <div className="flex-1 flex flex-row items-center justify-between">
                                        <span className="text-sm text-gray-600">Included in ABM 7 Drug Formulary</span>
                                        <span className="text-sm font-medium">No</span>
                                    </div>
                                </div>

                                <div className="flex flex-row items-center gap-x-3">
                                    <div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center">
                                        <span className="text-xs">💵</span>
                                    </div>
                                    <div className="flex-1 flex flex-row items-center justify-between">
                                        <span className="text-sm text-gray-600">Unit Markup</span>
                                        <span className="text-sm font-medium">15</span>
                                    </div>
                                </div>

                                <div className="flex flex-row items-center gap-x-3">
                                    <div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center">
                                        <span className="text-xs">💵</span>
                                    </div>
                                    <div className="flex-1 flex flex-row items-center justify-between">
                                        <span className="text-sm text-gray-600">Package Markup</span>
                                        <span className="text-sm font-medium">25</span>
                                    </div>
                                </div>

                                <div className="flex flex-row items-center gap-x-3">
                                    <div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center">
                                        <span className="text-xs">💰</span>
                                    </div>
                                    <div className="flex-1 flex flex-row items-center justify-between">
                                        <span className="text-sm text-gray-600">Thiqa Package Ref Price</span>
                                        <span className="text-sm font-medium">-</span>
                                    </div>
                                </div>

                                <div className="flex flex-row items-center gap-x-3">
                                    <div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center">
                                        <span className="text-xs">💰</span>
                                    </div>
                                    <div className="flex-1 flex flex-row items-center justify-between">
                                        <span className="text-sm text-gray-600">Thiqa Ref co-pay amount (package)</span>
                                        <span className="text-sm font-medium">-</span>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Right Column - Item Info (always visible) */}
                <div className="w-[40%] h-full">
                    {/* Content */}
                    <div className="p-6 space-y-6">
                        {rightPanelTab === "overview" && (
                            <div className="space-y-3">
                                <div className="flex flex-row items-start justify-between">
                                    <span className="text-sm text-gray-600">ITEM CODE</span>
                                    <span className="text-sm font-medium">{item.sku}</span>
                                </div>
                                <div className="flex flex-row items-start justify-between">
                                    <span className="text-sm text-gray-600">MANUFACTURER</span>
                                    <span className="text-sm">{item.manufacturer}</span>
                                </div>
                                <div className="flex flex-row items-start justify-between">
                                    <span className="text-sm text-gray-600">STORAGE CONDITION</span>
                                    <span className="text-sm">{item.storageCondition}</span>
                                </div>
                                <div className="flex flex-row items-start justify-between">
                                    <span className="text-sm text-gray-600">CATEGORY</span>
                                    <span className="text-sm">{item.category}</span>
                                </div>
                                <div className="flex flex-row items-start justify-between">
                                    <span className="text-sm text-gray-600">REGISTRATION NUMBER</span>
                                    <span className="text-sm">{item.registrationNumber}</span>
                                </div>
                                <div className="flex flex-row items-start justify-between">
                                    <span className="text-sm text-gray-600">SCIENTIFIC NAME (IUPAC)</span>
                                    <span className="text-sm">{item.scientificName}</span>
                                </div>
                                <div className="flex flex-row items-start justify-between">
                                    <span className="text-sm text-gray-600">ACTIVE INGREDIENT</span>
                                    <span className="text-sm text-blue-600">{item.activeIngredient}</span>
                                </div>
                                <div className="flex flex-row items-start justify-between">
                                    <span className="text-sm text-gray-600">DOSAGE FORM</span>
                                    <span className="text-sm">{item.dosageForm}</span>
                                </div>
                                <div className="flex flex-row items-start justify-between">
                                    <span className="text-sm text-gray-600">ADMINISTRATION</span>
                                    <span className="text-sm">{item.administration}</span>
                                </div>
                                <div className="flex flex-row items-start justify-between">
                                    <span className="text-sm text-gray-600">PHARMA CLASS</span>
                                    <span className="text-sm">{item.pharmaClass}</span>
                                </div>
                            </div>
                        )}

                        {rightPanelTab === "identifiers" && (
                            <div className="space-y-6">
                                {/* GTIN-13 (EAN) Section */}
                                <div>
                                    <h4 className="text-base font-semibold mb-2">GTIN-13 (EAN)</h4>
                                    <p className="text-sm text-gray-600 mb-3">Product barcodes</p>
                                    <div className="border rounded-lg p-3 bg-gray-50">
                                        <span className="text-sm font-mono">5901234123457</span>
                                    </div>
                                </div>

                                {/* GTIN-14 (ITF-14) Section */}
                                <div>
                                    <h4 className="text-base font-semibold mb-2">GTIN-14 (ITF-14)</h4>
                                    <p className="text-sm text-gray-600 mb-3">Custom barcodes</p>
                                    <div className="border rounded-lg p-3 bg-gray-50">
                                        <span className="text-sm font-mono">15901234123458</span>
                                    </div>
                                </div>

                                {/* SKU Section */}
                                <div>
                                    <h4 className="text-base font-semibold mb-2">SKU</h4>
                                    <p className="text-sm text-gray-600 mb-3">Internal stock keeping unit</p>
                                    <div className="border rounded-lg p-3 bg-gray-50">
                                        <span className="text-sm font-mono">PZR-IBU-200-N</span>
                                    </div>
                                </div>

                                {/* Edit GTINs Button */}
                                <div className="pt-4">
                                    <Button variant="ghost" className="w-full justify-center">
                                        Edit GTINs
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Clinical Notes */}
                        {item.clinicalNotes && (
                            <div>
                                <span className="text-base font-medium mb-3 block">Clinical Notes</span>
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <span className="text-sm">{item.clinicalNotes}</span>
                                </div>
                            </div>
                        )}

                        {/* Smart Substitution Engine */}
                        <div className="bg-gradient-to-r from-cyan-400 to-purple-400 rounded-lg p-6 text-white">
                            <h4 className="font-semibold text-lg mb-2">Smart Substitution Engine</h4>
                            <p className="text-sm mb-4 opacity-90">
                                Using the integrated AI model (similar to Sanas AI), we automatically suggest generic substitutes based on therapeutic equivalence and insurance coverage codes.
                            </p>
                            <Button variant="secondary" size="sm">
                                CONFIGURE SUBSTITUTE LOGIC
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InventoryDetailView;
